import {
  AuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { msalInstance } from './msalInstance';
import { apiTokenRequest } from './msalConfig';
import { queryClient } from '@/lib/queryClient';
import { clearSelectedTenantId } from '@/lib/tenantStorage';

export const LOGIN_PATH = '/login';
export const AUTH_REDIRECT_KEY = 'auth.redirect';
export const AUTH_ERROR_KEY = 'auth.error';
const INTERACTIVE_ATTEMPT_KEY = 'auth.interactive.attempted';

export const CONSENT_ERROR_MESSAGE =
  'Your organization has not granted this application access. Contact your IT administrator or try a different account.';
export const GENERIC_AUTH_ERROR_MESSAGE = 'Sign-in failed. Please try again.';
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

const CONSENT_ERROR_CODES = new Set(['consent_required']);
const CONSENT_ERROR_PATTERN = /AADSTS65001|AADSTS90094|AADSTS650052/;
const MISSING_SERVICE_PRINCIPAL_PATTERN = /AADSTS650052/;

export const isConsentError = (error: unknown): boolean =>
  error instanceof AuthError &&
  (CONSENT_ERROR_CODES.has(error.errorCode) ||
    CONSENT_ERROR_CODES.has(error.subError) ||
    CONSENT_ERROR_PATTERN.test(error.errorMessage ?? ''));

const isMissingServicePrincipalError = (error: unknown): boolean =>
  error instanceof AuthError && MISSING_SERVICE_PRINCIPAL_PATTERN.test(error.errorMessage ?? '');

export const storeAuthError = (message: string) => {
  sessionStorage.setItem(AUTH_ERROR_KEY, message);
};

export const peekAuthError = (): string | null => sessionStorage.getItem(AUTH_ERROR_KEY);

export const clearAuthError = () => {
  sessionStorage.removeItem(AUTH_ERROR_KEY);
};

export const clearInteractiveAttempt = () => {
  sessionStorage.removeItem(INTERACTIVE_ATTEMPT_KEY);
};

let inflightApiTokenRequest: Promise<AuthenticationResult> | null = null;
let recoveryStarted = false;

export const acquireApiTokenSilent = (account: AccountInfo): Promise<AuthenticationResult> => {
  inflightApiTokenRequest ??= msalInstance
    .acquireTokenSilent({ ...apiTokenRequest, account })
    .then((result) => {
      sessionStorage.removeItem(INTERACTIVE_ATTEMPT_KEY);
      recoveryStarted = false;
      return result;
    })
    .finally(() => {
      inflightApiTokenRequest = null;
    });
  return inflightApiTokenRequest;
};

let interactiveAcquisitionStarted = false;

const acquireApiTokenInteractive = async (account: AccountInfo) => {
  if (interactiveAcquisitionStarted) return;
  interactiveAcquisitionStarted = true;
  sessionStorage.setItem(INTERACTIVE_ATTEMPT_KEY, 'true');
  if (globalThis.location.pathname !== LOGIN_PATH) {
    sessionStorage.setItem(AUTH_REDIRECT_KEY, globalThis.location.href);
  }
  try {
    await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
  } catch {
    interactiveAcquisitionStarted = false;
    storeAuthError(GENERIC_AUTH_ERROR_MESSAGE);
    await redirectToLogin();
  }
};

export const recoverFromSilentTokenFailure = async (error: unknown, account: AccountInfo) => {
  if (recoveryStarted) return;
  recoveryStarted = true;
  if (isMissingServicePrincipalError(error)) {
    storeAuthError(CONSENT_ERROR_MESSAGE);
    await redirectToLogin();
    return;
  }
  const consentError = isConsentError(error);
  if (consentError || error instanceof InteractionRequiredAuthError) {
    if (sessionStorage.getItem(INTERACTIVE_ATTEMPT_KEY)) {
      storeAuthError(consentError ? CONSENT_ERROR_MESSAGE : GENERIC_AUTH_ERROR_MESSAGE);
      await redirectToLogin();
      return;
    }
    await acquireApiTokenInteractive(account);
    return;
  }
  storeAuthError(GENERIC_AUTH_ERROR_MESSAGE);
  await redirectToLogin();
};

export const redirectToLogin = async () => {
  clearSelectedTenantId();
  if (globalThis.location.pathname === LOGIN_PATH) {
    await msalInstance.clearCache();
    return;
  }
  sessionStorage.setItem(AUTH_REDIRECT_KEY, globalThis.location.href);
  await msalInstance.clearCache();
  queryClient.clear();
  globalThis.location.assign(LOGIN_PATH);
};

export const acquireAccessTokenOrRedirect = async (): Promise<string | null> => {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    await redirectToLogin();
    return null;
  }
  try {
    const response = await acquireApiTokenSilent(account);
    return response.accessToken;
  } catch (error) {
    await recoverFromSilentTokenFailure(error, account);
    return null;
  }
};
